import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtErr } from "../../lib/api";

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toMin(t) { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; }

const EMPTY = {
  name: "", email: "", phone: "", date: "", time: "19:00", party_size: "2",
  table_id: "", table_ids: null, dietary: "", occasion: "", notes: "", source: "walkin",
};

export default function AddBookingDialog({ restaurant, tables, open, onOpenChange, preset, onAdded }) {
  const [addForm, setAddForm] = useState(EMPTY);
  const [suggest, setSuggest] = useState(null);
  const [dayBookings, setDayBookings] = useState([]);

  useEffect(() => {
    if (!open || !addForm.date) return;
    api.get(`/restaurants/${restaurant.id}/bookings`, { params: { date: addForm.date } })
      .then((r) => setDayBookings(r.data)).catch(() => setDayBookings([]));
  }, [open, addForm.date, restaurant.id]);

  useEffect(() => {
    if (open) {
      setAddForm({ ...EMPTY, date: preset?.date || todayLocal(), time: preset?.time || "19:00", table_id: preset?.table_id || "" });
    }
  }, [open]);

  useEffect(() => {
    if (!open || !addForm.date || !addForm.time) { setSuggest(null); return; }
    api.get(`/restaurants/${restaurant.id}/suggest-table`, {
      params: { date: addForm.date, time: addForm.time, party_size: Number(addForm.party_size) || 2 },
    }).then((r) => setSuggest(r.data)).catch(() => setSuggest(null));
  }, [open, addForm.date, addForm.time, addForm.party_size, restaurant.id]);

  const combos = [];
  const seenCombo = new Set();
  tables.forEach((t) => (t.combines_with || []).forEach((oid) => {
    const o = tables.find((x) => x.id === oid);
    if (!o || !o.active || !t.active) return;
    const [a, b] = [t, o].sort((x, y) => (x.id < y.id ? -1 : 1));
    const key = a.id + "|" + b.id;
    if (seenCombo.has(key)) return;
    seenCombo.add(key);
    combos.push({ ids: [a.id, b.id], label: `${a.label}+${b.label}`, capacity: a.capacity + b.capacity });
  }));

  const duration = restaurant.booking_policy?.slot_duration ?? 90;
  const occupiedIds = new Set();
  if (addForm.time) {
    const s0 = toMin(addForm.time);
    dayBookings.filter((b) => ["confirmed", "seated", "pending"].includes(b.status)).forEach((b) => {
      const bs = toMin(b.time), be = bs + (b.duration || duration) + 15;
      if (bs < s0 + duration + 15 && s0 < be) {
        (b.table_ids || [b.table_id]).filter(Boolean).forEach((tid) => {
          occupiedIds.add(tid);
          const tt = tables.find((x) => x.id === tid);
          if (tt?.parent_id) occupiedIds.add(tt.parent_id);
          tables.forEach((x) => { if (x.parent_id === tid) occupiedIds.add(x.id); });
        });
      }
    });
  }
  const freeTables = tables.filter((t) => t.active && !occupiedIds.has(t.id));
  const selIds = addForm.table_ids || (addForm.table_id ? [addForm.table_id] : []);
  const selCap = tables.filter((t) => selIds.includes(t.id)).reduce((n, t) => n + (t.capacity || 0), 0);

  const manualAdd = async () => {
    try {
      await api.post(`/restaurants/${restaurant.id}/bookings`, {
        name: addForm.name, email: addForm.email, phone: addForm.phone,
        date: addForm.date, time: addForm.time, party_size: Number(addForm.party_size),
        table_id: addForm.table_id || null, table_ids: addForm.table_ids,
        dietary: addForm.dietary, occasion: addForm.occasion, notes: addForm.notes,
        source: addForm.source,
      });
      toast.success("Booking added");
      onOpenChange(false);
      onAdded?.();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border/60 max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-outfit">Add booking manually</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2" data-testid="add-booking-form">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Booking type</Label>
            <Select value={addForm.source} onValueChange={(v) => setAddForm({ ...addForm, source: v })}>
              <SelectTrigger data-testid="add-source-select" className="mt-1.5 bg-background border-border/60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="walkin">Walk-in</SelectItem>
                <SelectItem value="phone">Phone booking</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              {addForm.source === "walkin" ? "Nothing is mandatory — name and contact details optional." : "Name, email and phone are required, like an online booking."}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Date</Label>
              <Input data-testid="add-date-input" type="date" value={addForm.date}
                min={todayLocal()}
                onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                className="mt-1.5 bg-background border-border/60" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Time</Label>
              <Input data-testid="add-time-input" type="time" step="1800" value={addForm.time}
                onChange={(e) => setAddForm({ ...addForm, time: e.target.value })}
                className="mt-1.5 bg-background border-border/60" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Covers</Label>
              <Input data-testid="add-party-input" type="number" min="1" value={addForm.party_size}
                onChange={(e) => setAddForm({ ...addForm, party_size: e.target.value })}
                className="mt-1.5 bg-background border-border/60" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Table (optional)</Label>
              <Select value={addForm.table_ids ? `combo:${addForm.table_ids.join(",")}` : (addForm.table_id || "auto")}
                onValueChange={(v) => {
                  if (v.startsWith("combo:")) setAddForm({ ...addForm, table_id: "", table_ids: v.slice(6).split(",") });
                  else setAddForm({ ...addForm, table_id: v === "auto" ? "" : v, table_ids: null });
                }}>
                <SelectTrigger data-testid="add-table-select" className="mt-1.5 bg-background border-border/60">
                  <SelectValue placeholder="Auto-assign" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-assign</SelectItem>
                  {tables.filter((t) => t.active).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label} ({t.capacity})</SelectItem>
                  ))}
                  {combos.filter((c) => c.capacity >= Number(addForm.party_size || 1)).map((c) => (
                    <SelectItem key={c.ids.join("|")} value={`combo:${c.ids.join(",")}`}>{c.label} ({c.capacity}, combined)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!addForm.table_id && !addForm.table_ids && suggest?.best && (
                <button type="button" data-testid="apply-suggested-table"
                  onClick={() => setAddForm(suggest.best.ids
                    ? { ...addForm, table_id: "", table_ids: suggest.best.ids }
                    : { ...addForm, table_id: suggest.best.id, table_ids: null })}
                  className="mt-1.5 text-xs text-primary hover:underline text-left">
                  Suggested: {suggest.best.label} (seats {suggest.best.capacity}){suggest.best.ids ? " — combined tables" : ""} — tap to use
                </button>
              )}
              {!addForm.table_id && !addForm.table_ids && suggest && !suggest.best && (
                <p data-testid="no-suggested-table" className="mt-1.5 text-xs text-amber-500">No table free at this time</p>
              )}
            </div>
          </div>
          <div data-testid="combine-free-section">
            <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Combine tables (optional)</Label>
            <p className="text-[11px] text-muted-foreground mt-1">
              Tick any free tables for a larger party — {selCap} seat{selCap === 1 ? "" : "s"} selected for {addForm.party_size} guest{Number(addForm.party_size) === 1 ? "" : "s"}.
            </p>
            <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto dash-scroll" data-testid="combine-free-list">
              {freeTables.map((t) => {
                const on = selIds.includes(t.id);
                return (
                  <button key={t.id} type="button" data-testid={`combine-free-${t.id}`}
                    onClick={() => {
                      const next = on ? selIds.filter((x) => x !== t.id) : [...selIds, t.id];
                      setAddForm({ ...addForm, table_id: "", table_ids: next.length ? next : null });
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}>
                    {t.parent_id ? "└ " : ""}{t.label} ({t.capacity})
                  </button>
                );
              })}
            </div>
          </div>
          <Input data-testid="add-name-input" placeholder={addForm.source === "walkin" ? "Guest name (optional)" : "Guest name"} value={addForm.name}
            onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="bg-background border-border/60" />
          <Input data-testid="add-email-input" type="email" placeholder={addForm.source === "walkin" ? "Guest email (optional)" : "Guest email"} value={addForm.email}
            onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} className="bg-background border-border/60" />
          <Input data-testid="add-phone-input" placeholder={addForm.source === "walkin" ? "Phone (optional)" : "Phone"} value={addForm.phone}
            onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} className="bg-background border-border/60" />
          <Input data-testid="add-dietary-input" placeholder="Dietary requirements (e.g. vegan, nut allergy)" value={addForm.dietary}
            onChange={(e) => setAddForm({ ...addForm, dietary: e.target.value })} className="bg-background border-border/60" />
          <Input data-testid="add-occasion-input" placeholder="Occasion (e.g. birthday, anniversary)" value={addForm.occasion}
            onChange={(e) => setAddForm({ ...addForm, occasion: e.target.value })} className="bg-background border-border/60" />
          <Textarea data-testid="add-notes-input" rows={2} placeholder="Notes (optional)" value={addForm.notes}
            onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} className="bg-background border-border/60" />
          <Button data-testid="add-booking-submit-btn" onClick={manualAdd}
            disabled={!addForm.date || !addForm.time || (addForm.source === "phone" && (!addForm.name.trim() || !addForm.email.trim() || !addForm.phone.trim()))}
            className="w-full rounded-full">
            Add booking
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
